import { auth } from '@/services/firebase';
import { accountEmailStore } from '@/services/accountEmailStore';
import { Feature, ReleaseMode } from './featureFlags.definition';
import type { FeatureFlagDefinition } from './featureFlags.definition';
import { featureFlags, internalReleaseEmailPatterns } from './featureFlags.state';

const isInternalEmail = (email?: string | null): boolean =>
  Boolean(email) && internalReleaseEmailPatterns.some((pattern) => pattern.test(email as string));

/**
 * Runtime feature flag evaluator
 *
 * Centralized access layer for feature toggles.
 * Ensures consistent behavior across UI, API, and services.
 *
 * When a flag's `releaseMode` is `INTERNAL`, only a CONFIRMED email counts - an unverified
 * address could belong to anyone, so it can't be trusted to gate internal access. The
 * account email is checked first (covers both business and personal accounts - AuthProvider
 * only ever surfaces one email per account) since it's backend-verified, falling back to the
 * Firebase login email (via its own `emailVerified` flag) when AuthProvider's account fetch
 * hasn't resolved yet - if neither is confirmed, access is denied.
 */
export const isFeatureEnabled = (feature: Feature): boolean => {
  const flag: FeatureFlagDefinition | undefined = featureFlags[feature];
  if (!flag?.enabled) return false;

  const releaseMode = flag.releaseMode ?? ReleaseMode.PUBLIC;
  if (releaseMode === ReleaseMode.PUBLIC) return true;

  const accountEmail = accountEmailStore.get();
  const confirmedEmail = accountEmail.confirmed
    ? accountEmail.email
    : auth.currentUser?.emailVerified
      ? auth.currentUser?.email
      : undefined;

  return isInternalEmail(confirmedEmail);
};
